import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { createUserTeam, type UserDetail } from '../queries/admin';
import { Banner, Button, Input } from './ui';

/**
 * A team of their own, on us, for somebody who signed up on their own and
 * has no team. Every subscriber has a team, paid or given; this is how the
 * given kind reaches a person who already exists. The personal grant on the
 * card beside this one is the older arrangement: it puts the plan on the
 * person with no team behind it, and the app has nowhere to show it.
 */

function isoFromDateInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function tomorrow(): string {
  const date = new Date(Date.now() + 86_400_000);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function TeamGiftCard({ user, invalidates }: { user: UserDetail; invalidates: ReadonlyArray<readonly unknown[]> }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [seats, setSeats] = useState('1');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState(user.access.comp?.reason ?? '');

  const give = useMutation({
    mutationFn: () =>
      createUserTeam(user.id, {
        name: name.trim() || null,
        seats: Number(seats) || 1,
        until: isoFromDateInput(until),
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      track('Comp Team Given', { seats: Number(seats) || 1 });
      return Promise.all(invalidates.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    give.mutate();
  };
  const first = user.name?.trim().split(/\s+/)[0] || user.email.split('@')[0];

  return (
    <form onSubmit={submit} className="card overflow-hidden">
      <div className="px-4 pt-4">
        <h2 className="text-h4 text-chalk">A team of their own, on us</h2>
        <p className="mt-1 text-body text-chalk-dim">
          Makes a comped team with {first} as owner. Every subscriber has a team, whether they pay for it or we give
          it; the plan lives there, and the app shows it there.
          {user.access.comp?.active ? ' Their personal grant ends when the team takes over.' : ''}
        </p>
      </div>
      <div className="mt-4 border-t border-hairline px-4 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_7rem_11rem] sm:items-end">
          <Input
            label="Team name (optional)"
            autoComplete="off"
            maxLength={80}
            placeholder={`${first}'s team`}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input label="Seats" type="number" min={1} max={1000} step={1} inputMode="numeric" required value={seats} onChange={(event) => setSeats(event.target.value)} />
          <Input label="Expires" type="date" min={tomorrow()} value={until} onChange={(event) => setUntil(event.target.value)} />
        </div>
        <div className="mt-3">
          <Input
            label="Reason"
            placeholder="Pilot agreed by email, friend of the house…"
            maxLength={200}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <p className="mt-2 text-caption text-chalk-faint">
          One seat is just them. A blank expiry never lapses. They can rename the team from Settings.
        </p>
      </div>
      <div className="flex items-center justify-end border-t border-hairline bg-slate-900/40 px-4 py-3">
        <Button type="submit" variant="primary" busy={give.isPending}>
          Make the team
        </Button>
      </div>
      {give.isError ? (
        <div className="px-4 pb-4">
          <Banner tone="danger">{errorMessage(give.error)}</Banner>
        </div>
      ) : null}
      {give.isSuccess ? (
        <div className="px-4 pb-4">
          <Banner tone="go" title={`${give.data.name} is theirs, on us.`}>
            The team is on the plan with {give.data.seats.comped} comped {give.data.seats.comped === 1 ? 'seat' : 'seats'}.
          </Banner>
        </div>
      ) : null}
    </form>
  );
}
